/**
 * What: Reconciles Shopify managed-pricing returns with the TestFeed plan assignment.
 * Transport / data-flow: Reads the optional `plan_handle` from the current embedded-app URL, posts it to the authenticated Shopify Worker, then refreshes the shared testing-capacity query. The Worker's durable activation lifecycle (issue #1312) drives the merchant-facing state across refreshes.
 *
 */
import { useEffect, useRef, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import type { ShopifyAppBridge } from '../shopifyApiFetch'
import { shopifyApiFetch } from '../shopifyApiFetch'
import { buildShopifyAppScopeKey, useShopifyAppContext } from '../ShopifyAppContext'
import { useShopifyScopeLifecycle } from './useShopifyScopeLifecycle'
import { testFeedUsageActivityQueryKey } from './useTestFeedUsageActivity'
import { testFeedUsageQueryKey as buildUsageQueryKey } from './useTestFeedUsage'
import { shopifyEntitlementsQueryKey } from './useShopifyEntitlements'

export type ShopifyPlanSyncState = { tone: 'success' | 'pending' | 'attention'; message: string } | null
export type ShopifyPlanActivationStatus = 'pending' | 'stalled' | null

/** Maps a plan-sync banner tone onto the Polaris Banner tone shared by Home and Settings. */
export function planSyncBannerTone(tone: 'success' | 'pending' | 'attention') {
  if (tone === 'success') return 'success' as const
  if (tone === 'attention') return 'warning' as const
  return 'info' as const
}

function planName(planCode: unknown) {
  if (planCode === 'free-v1') return 'Free'
  if (planCode === 'plus-v1') return 'Plus'
  return 'selected'
}

function isAssignedPlanSyncResult(value: unknown): value is { status: string; planCode?: unknown } {
  return (
    typeof value === 'object' &&
    value !== null &&
    typeof (value as { status?: unknown }).status === 'string' &&
    ['assigned', 'changed', 'unchanged'].includes((value as { status: string }).status)
  )
}

function didPlanSyncChange(value: unknown) {
  if (typeof value !== 'object' || value === null) return false
  const payload = value as { status?: unknown; deadlineUpdated?: unknown }
  return (
    ['assigned', 'changed', 'interval_changed', 'no_subscription'].includes(payload.status as string) ||
    payload.deadlineUpdated === true
  )
}

type PlanSyncActivation = { status: 'pending' | 'stalled' | 'confirmed' | 'scheduled'; planHandle: string }

function readActivation(payload: unknown): PlanSyncActivation | null {
  if (typeof payload !== 'object' || payload === null) return null
  const activation = (payload as { activation?: unknown }).activation
  if (typeof activation !== 'object' || activation === null) return null
  const { status, planHandle } = activation as { status?: unknown; planHandle?: unknown }
  if (typeof planHandle !== 'string') return null
  if (status !== 'pending' && status !== 'stalled' && status !== 'confirmed' && status !== 'scheduled') return null
  return { status, planHandle }
}

function getPlanHandle() {
  return new URLSearchParams(globalThis.window?.location.search ?? '').get('plan_handle')
}

function removePlanHandle() {
  const currentUrl = new URL(globalThis.window.location.href)
  currentUrl.searchParams.delete('plan_handle')
  globalThis.window.history.replaceState(null, '', `${currentUrl.pathname}${currentUrl.search}${currentUrl.hash}`)
}

const PENDING_MESSAGE = 'Your plan is being activated — this can take a moment.'
const STALLED_MESSAGE = "We couldn't confirm your plan with Shopify yet. Check the status, or try again shortly."

/** Just above the Worker's 60s pending-recheck debounce so every poll performs a real Partner verification. */
export const PLAN_ACTIVATION_POLL_INTERVAL_MS = 65 * 1000

/** Reconciles a managed-pricing return once per mounted Shopify app route and refreshes its shared capacity state. */
export function useShopifyPlanSync(shopify: ShopifyAppBridge) {
  const [planSyncState, setPlanSyncState] = useState<ShopifyPlanSyncState>(null)
  const [planActivation, setPlanActivation] = useState<ShopifyPlanActivationStatus>(null)
  const [planSyncSettled, setPlanSyncSettled] = useState(false)
  const hasStarted = useRef(false)
  const queryClient = useQueryClient()
  const { shopDomain, orgId, connectionId } = useShopifyAppContext()
  const { token: scopeToken, isActive } = useShopifyScopeLifecycle(
    buildShopifyAppScopeKey({ shopDomain, orgId, connectionId }),
  )
  const syncPlanMutation = useMutation({
    mutationFn: async ({ planHandle, requestScopeToken }: { planHandle: string | null; requestScopeToken: string }) => {
      const response = await shopifyApiFetch(shopify, '/api/billing/plan-sync', {
        init: {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(planHandle ? { planHandle } : {}),
        },
      })
      const payload = (await response.json()) as unknown
      if (!response.ok) throw new Error(`Billing plan sync failed (${response.status})`)
      return { payload, planHandle, scope: { shopDomain, orgId, connectionId }, scopeToken: requestScopeToken }
    },
    onSuccess: async ({ payload, planHandle, scope: originScope, scopeToken: originScopeToken }) => {
      const activation = readActivation(payload)
      if (didPlanSyncChange(payload) || activation?.status === 'confirmed') {
        await Promise.all([
          queryClient.invalidateQueries({
            queryKey: buildUsageQueryKey(originScope.shopDomain, originScope.orgId, originScope.connectionId),
          }),
          queryClient.invalidateQueries({
            queryKey: testFeedUsageActivityQueryKey(
              originScope.shopDomain,
              originScope.orgId,
              originScope.connectionId,
            ),
          }),
          queryClient.invalidateQueries({
            queryKey: shopifyEntitlementsQueryKey(
              originScope.shopDomain,
              originScope.orgId,
              originScope.connectionId,
              true,
            ),
          }),
        ])
      }
      if (!isActive(originScopeToken)) return
      setPlanActivation(activation?.status === 'pending' || activation?.status === 'stalled' ? activation.status : null)
      if (activation?.status === 'confirmed') {
        const planCode = (payload as { planCode?: unknown }).planCode ?? activation.planHandle
        setPlanSyncState({ tone: 'success', message: `Your ${planName(planCode)} plan is active.` })
        return
      }
      if (activation?.status === 'scheduled') {
        setPlanSyncState({
          tone: 'success',
          message: `Your ${planName(activation.planHandle)} plan is scheduled to start at your next billing cycle.`,
        })
        return
      }
      if (isAssignedPlanSyncResult(payload) && planHandle) {
        setPlanSyncState({ tone: 'success', message: `Your ${planName(payload.planCode)} plan is active.` })
        return
      }
      if (activation?.status === 'stalled') {
        setPlanSyncState({ tone: 'attention', message: STALLED_MESSAGE })
        return
      }
      if (activation?.status === 'pending' || planHandle) {
        setPlanSyncState({ tone: 'pending', message: PENDING_MESSAGE })
      }
    },
    onError: (error, { planHandle, requestScopeToken }) => {
      console.error(error)
      if (planHandle && isActive(requestScopeToken)) {
        setPlanActivation('pending')
        setPlanSyncState({ tone: 'pending', message: PENDING_MESSAGE })
      }
    },
    onSettled: (_data, _error, { requestScopeToken }) => {
      if (isActive(requestScopeToken)) setPlanSyncSettled(true)
    },
  })

  useEffect(() => {
    hasStarted.current = false
    setPlanSyncState(null)
    setPlanActivation(null)
    setPlanSyncSettled(false)
  }, [scopeToken])

  useEffect(() => {
    const planHandle = getPlanHandle()
    if (hasStarted.current) return
    if (!planHandle && (!orgId || !connectionId)) {
      // No TestFeed connection yet: skip the no-handle sync instead of burning the
      // Worker's debounce window on a call guaranteed to 404. The skip leaves
      // hasStarted unset so resolving scope below still fires exactly once.
      setPlanSyncSettled(true)
      return
    }
    hasStarted.current = true
    if (planHandle) removePlanHandle()

    syncPlanMutation.mutate({ planHandle, requestScopeToken: scopeToken })
  }, [scopeToken, syncPlanMutation, orgId, connectionId])

  const { mutate: syncPlan, isPending: isSyncing } = syncPlanMutation

  // Auto-poll only while pending: stalled activations stop polling and hand control to Check status.
  useEffect(() => {
    if (planActivation !== 'pending') return
    const interval = setInterval(
      () => syncPlan({ planHandle: null, requestScopeToken: scopeToken }),
      PLAN_ACTIVATION_POLL_INTERVAL_MS,
    )
    return () => clearInterval(interval)
  }, [planActivation, scopeToken, syncPlan])

  const checkPlanStatus = () => {
    if (isSyncing) return
    syncPlan({ planHandle: null, requestScopeToken: scopeToken })
  }

  return {
    planSyncState,
    planSyncSettled,
    planActivation,
    checkPlanStatus,
    isCheckingPlanStatus: syncPlanMutation.isPending,
  }
}
